import * as pdfjsLib from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.mjs?url'
import { createWorker } from 'tesseract.js'
import type { ParsedTicket } from './types'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker

const MONTHS: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
  JANUARY: '01', FEBRUARY: '02', MARCH: '03', APRIL: '04', JUNE: '06',
  JULY: '07', AUGUST: '08', SEPTEMBER: '09', OCTOBER: '10', NOVEMBER: '11', DECEMBER: '12',
}

const MONTH_PATTERN = 'JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC'

function toIso(month: string, day: string, year: string) {
  return `${year}-${MONTHS[month.toUpperCase()]!}-${day.padStart(2, '0')}`
}

function inferCompactDateYear(month: string, day: string, referenceYear: number) {
  // Emirates itinerary dates are often printed as "MON 12OCT" without a year.
  // The ticket issuance date supplies the year; the itinerary can cross New Year.
  return toIso(month, day, String(referenceYear))
}

export function prettyDate(iso: string) {
  const [year, month, day] = iso.split('-')
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12)
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(date)
}

export function firstTwoNameParts(value: string) {
  // Many airline documents print names as "SURNAME, GIVEN FATHER [GRANDFATHER]".
  // The reservation needs the person's given name and father's name, not the
  // surname/grandfather portion before the comma.
  const commaName = value.includes(',') ? value.slice(value.indexOf(',') + 1) : value
  return commaName
    .replace(/[\/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .join(' ')
}

async function extractOcrText(pdf: pdfjsLib.PDFDocumentProxy): Promise<string> {
  const worker = await createWorker('eng')
  const pages: string[] = []
  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 2 })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) continue
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      await page.render({ canvasContext: context, viewport }).promise
      const result = await worker.recognize(canvas)
      pages.push(result.data.text)
      canvas.width = 0
      canvas.height = 0
    }
  } finally {
    await worker.terminate()
  }
  return pages.join('\n')
}

export async function extractTicket(file: File): Promise<ParsedTicket> {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    throw new Error('Please upload a PDF flight ticket.')
  }

  const data = new Uint8Array(await file.arrayBuffer())
  const pdf = await pdfjsLib.getDocument({ data }).promise
  let text = ''
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber)
    const content = await page.getTextContent()
    text += `${content.items.map((item) => ('str' in item ? item.str : '')).join(' ')}\n`
  }

  let normalized = text.replace(/\s+/g, ' ').trim()
  // Some airline confirmations, including Ethiopian's printable e-ticket receipt,
  // are scanned PDFs with no text layer. OCR runs locally in the browser only when
  // the PDF text layer is empty or too short to contain ticket details.
  if (normalized.length < 80) {
    const ocrText = await extractOcrText(pdf)
    normalized = ocrText.replace(/\s+/g, ' ').trim()
    text = ocrText
  }

  const weekdays = new Set(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
  const monthTokens = new Set(Object.keys(MONTHS))
  const nameCandidates = [...normalized.matchAll(/\b([A-Z][A-Z'/-]+,\s+[A-Z][A-Z'/-]+(?:\s+[A-Z][A-Z'/-]+)*)\b/g)]
  const travelportName = nameCandidates.find((candidate) => {
    const parts = candidate[1]!.split(',').map((part) => part.trim())
    const surname = parts[0] ?? ''
    const given = parts[1] ?? ''
    return !weekdays.has(surname) && !monthTokens.has(given) && given.split(/\s+/).length >= 2
  })
  // OCR may read the receipt's top row column labels before reading the values,
  // producing text such as "Passenger Name: e-Ticket Number: ... AWANO, MESERET
  // BEKELE ...". Never treat those labels as the passenger name; prefer the
  // comma-formatted passenger value when it is present.
  const ethiopianName = normalized.match(/\b([A-Z][A-Z'/-]+,\s*[A-Z][A-Z'/-]+(?:\s+[A-Z][A-Z'/-]+)*)\b/)
  const flydubaiName = normalized.match(/\b(?:Ms|Mr|Mrs|Miss|Dr)\.?\s+([A-Za-z]+(?:\s+[A-Za-z]+){1,4})\b/i)
  const cleanedFlydubaiName = flydubaiName?.[1]?.replace(/\s+(?:Primary Adult|Primary|Adult)\b.*$/i, '').trim()
  const airArabiaName = normalized.match(/\bMs\.?\s+([A-Za-z]+(?:\s+[A-Za-z]+){2})\s+E-?ticket\s+number\b/i)
  // Travelport date headings also contain commas (for example, "MON, OCT 12"),
  // so use the validated Travelport passenger candidate before the generic
  // Ethiopian comma-formatted fallback.
  const nameMatch = travelportName?.[1] ?? ethiopianName?.[1] ?? airArabiaName?.[1] ?? cleanedFlydubaiName

  const shortDatePattern = /\b(?:MON|TUE|WED|THU|FRI|SAT|SUN),?\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{1,2}),\s+(\d{4})\b/gi
  const longDatePattern = /\b(\d{1,2})\s+(JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)\s+(\d{4}),?\s+(?:MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY)\b/gi
  const dates: string[] = []
  for (const match of normalized.matchAll(shortDatePattern)) {
    const iso = toIso(match[1]!, match[2]!, match[3]!)
    if (!dates.includes(iso)) dates.push(iso)
  }
  for (const match of normalized.matchAll(longDatePattern)) {
    const iso = toIso(match[2]!, match[1]!, match[3]!)
    if (!dates.includes(iso)) dates.push(iso)
  }

  // Ethiopian printable receipts place the departure and arrival dates in the
  // flight heading: "Flight - ... - October 22, 2026 - October 23, 2026".
  const flightHeadingPattern = new RegExp(
    `Flight\\s*-\\s*[^-]+\\s*-\\s*[^-]+\\s*-\\s*(${MONTH_PATTERN})\\s+(\\d{1,2}),\\s*(\\d{4})(?:\\s*-\\s*(${MONTH_PATTERN})\\s+(\\d{1,2}),\\s*(\\d{4}))?`,
    'gi',
  )
  for (const match of normalized.matchAll(flightHeadingPattern)) {
    const departure = toIso(match[1]!, match[2]!, match[3]!)
    if (!dates.includes(departure)) dates.push(departure)
    if (match[4]) {
      const arrival = toIso(match[4], match[5]!, match[6]!)
      if (!dates.includes(arrival)) dates.push(arrival)
    }
  }

  // Air Arabia confirmations print itinerary dates as "11 Sep 2026 01:10"
  // without a weekday, and may repeat the same date for departure and arrival.
  const itineraryText = normalized.match(/Travel Itinerary([\s\S]*)/i)?.[1] ?? normalized
  const calendarDatePattern = new RegExp(`\\b(\\d{1,2})\\s+(${MONTH_PATTERN})\\s+(\\d{4})\\b`, 'gi')
  for (const match of itineraryText.matchAll(calendarDatePattern)) {
    const iso = toIso(match[2]!, match[1]!, match[3]!)
    if (!dates.includes(iso)) dates.push(iso)
  }

  const issuanceDate = normalized.match(/\b(?:ISSUANCE\s+DATE\s*)?(\d{1,2})(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(\d{2})\b/i)
  const pageDate = normalized.match(/\b\d{1,2}\/\d{1,2}\/(\d{2})\b/)
  const referenceYear = issuanceDate
    ? 2000 + Number(issuanceDate[3]!)
    : pageDate ? 2000 + Number(pageDate[1]!) : new Date().getFullYear()
  const compactDatePattern = /\b(?:MON|TUE|WED|THU|FRI|SAT|SUN),?\s+(\d{1,2})(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/gi
  const compactDates = [...normalized.matchAll(compactDatePattern)]
  let previousMonth = -1
  let year = referenceYear
  for (const match of compactDates) {
    const monthNumber = Number(MONTHS[match[2]!.toUpperCase()])
    if (previousMonth >= 10 && monthNumber <= 2) year += 1
    previousMonth = monthNumber
    const iso = inferCompactDateYear(match[2]!, match[1]!, year)
    if (!dates.includes(iso)) dates.push(iso)
  }

  const looksLikeTicket = /Travelport|ViewTrip|PASSENGERS|Confirmation Number|eTicket Number|E-ticket number|flydubai|Air\s*Arabia|Confirmed Reservation|Your booking is confirmed|Passenger details|Emirates Record Locator|Electronic Ticket|Ethiopian Airlines|Passenger\s+Name/i.test(normalized)
  if (!looksLikeTicket) throw new Error('This PDF does not look like a supported flight ticket.')
  if (!nameMatch) throw new Error('Could not find the ticket owner name.')
  if (dates.length < 2) throw new Error('Could not find both travel dates.')

  return {
    // Keep only the person's name and father's name; omit a third (grandfather) name.
    guestName: firstTwoNameParts(nameMatch),
    arrivalDate: dates[0]!,
    departureDate: dates[dates.length - 1]!,
    rawText: text,
  }
}
